import { useTranslation } from 'react-i18next';

import { Stack, styled, Typography } from '@mui/material';

import Alert from '../../../../components/Alert';
import AlertButton from '../../../../components/AlertButton';
import CourseUserCell from '../../../../components/CourseUserCell';
import Tag from '../../../../components/Tag/index';
import { CourseMemberStatus, CourseRole } from '../../../../db/shared/types';
import { i18nNS } from '../../../../i18n';
import Trash2Icon from '../../../../icons/Trash2Icon';
import { toSentenceCase } from '../../../../utils/string';
import useCourseTeamUserVM from './vm';

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

const Role = styled(Tag)(() => ({
  textTransform: 'capitalize',
}));

interface Props {
  avatarId: MongoId;
  userId: MongoId;
  role: CourseRole;
  status: CourseMemberStatus;
}

const CourseTeamUser = ({ avatarId, userId, role, status }: Props) => {
  const { t } = useTranslation([i18nNS.COURSES, i18nNS.GLOSSARY]);

  const { currentUser, deleteMember } = useCourseTeamUserVM();

  return (
    <CourseUserCell
      isEmailIdNeeded
      isCourseRoleNeeded
      existingCourseRole={role}
      avatar={avatarId}
      title={(name, emailId, courseRole) =>
        name ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
            <Title variant="paragraphMedium">
              {userId === currentUser.userId ? t('you', { ns: i18nNS.GLOSSARY }) : name}
            </Title>
            <Role
              color="success"
              variant="outlined"
              // No need to check for isRemovedMember as removed members are not displayed
              label={courseRole === CourseRole.TA ? courseRole.toUpperCase() : courseRole}
            />
          </Stack>
        ) : null
      }
      subtitle={(name, emailId) => (
        <Typography variant="paragraphRegular">
          {status === CourseMemberStatus.ACTIVE ? emailId : toSentenceCase(status)}
        </Typography>
      )}
      action={
        <AlertButton color="error" variant="text">
          <Trash2Icon color="warning" />
          <Alert maxWidth="xs">
            <Alert.Body>
              {t('are_you_sure_you_want_to_delete_this_team_member', { ns: i18nNS.COURSES })}
            </Alert.Body>
            <Alert.Footer>
              <Alert.Action variant="text">{t('no', { ns: i18nNS.GLOSSARY })}</Alert.Action>
              <Alert.Action variant="text" color="error" onClick={deleteMember(userId)}>
                {t('yes', { ns: i18nNS.GLOSSARY })}
              </Alert.Action>
            </Alert.Footer>
          </Alert>
        </AlertButton>
      }
    />
  );
};

export default CourseTeamUser;
